import dayjs from "dayjs";
import { t } from "@/plugins/i18n";
import { isPickMetric } from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import DocumentDO = App.Data.Explorer.DocumentDO;

export function formatDateValues(dateValues: object) {
	return Object.values(dateValues).map((value) => {
		return dayjs(value, "YYYY-MM-DD", true).isValid()
			? dayjs(value).format("DD MMM YYYY")
			: value;
	});
}

/**
 * Builds the human-readable title for a document's single metric — the same
 * label SingleMetricDisplay computes and emits to its parent. Returns an empty
 * string when the document has no single metric configured.
 *
 * When a `percentage` other than 100 is supplied (e.g. a progress card's target
 * scaled to a percentage of the metric), it is prefixed as "80% of ...".
 */
export function buildSingleMetricTitle(
	document?: DocumentDO | null,
	percentage?: number | null
): string {
	if (!document?.single_metric) {
		return "";
	}

	const metric = document.single_metric;
	const propertyTitle = t(`properties.${metric.metric_name}.title`);

	let comparisonSuffix = "";
	if (metric.comparison_value !== "Y") {
		comparisonSuffix = metric.value_column.startsWith("delta")
			? ` (compared to ${metric.comparison_value})`
			: ` (${metric.comparison_value})`;
	}

	let title: string;
	if (isPickMetric(metric)) {
		const infixLabel =
			document.data_tables[metric.table_index].dimensions[0].type === "datetime"
				? "at"
				: "for";

		title =
			`${propertyTitle} ${infixLabel} ` +
			formatDateValues(metric.dimension_values).join(", ") +
			comparisonSuffix;
	} else {
		const singleMetricLabel = t(`explorer.single-metric.type.${metric.type}`);
		title = `${singleMetricLabel} ${propertyTitle.toLowerCase()}${comparisonSuffix}`;
	}

	if (percentage !== null && percentage !== undefined && percentage !== 100) {
		return `${percentage}% of ${title}`;
	}

	return title;
}
